"use client";

/**
 * `<PlanCard>` — single subscription tier card.
 *
 * Displays:
 * - Tier name + tag
 * - Big price + period suffix (e.g. "$29 /mo")
 * - Primary CTA button (full-width)
 * - Feature list (green check)
 * - Optional V2 features (gold "V2" pill, muted-but-readable text) —
 *   the dispatch's "Coming in V2 (included in your Premium
 *   subscription)" group renders inside this same list with a header.
 * - Optional "missing" features (line-through, x-icon) for tier
 *   differentiation
 *
 * Featured (boolean): adds gradient surface + blue border + lifted
 * shadow + "MOST POPULAR" pill. Premium tier should be featured for
 * Compass.
 */

import { memo, useState, type ReactNode } from "react";
import { Btn } from "../btn";
import { ArrowR } from "../icons";

export interface PlanFeature {
  text: string;
}

export interface PlanCardProps {
  name: string;
  nameColor?: string;
  tag: string;
  /** Numeric price (or null for "Coming Soon" Premium Reports). */
  price: number | null;
  /** Always shown in tabular-nums next to the $; can be a fractional value. */
  formatPrice?: (price: number) => string;
  /** e.g. "/mo" or "/year". */
  suffix: string;
  /** Optional secondary line under the price (e.g. "$99.99 billed annually"). */
  priceFootnote?: string;
  /** Optional "MOST POPULAR" / "BEST VALUE" badge. */
  badge?: string;
  /** Featured = gradient surface, blue border, lifted shadow. */
  featured?: boolean;
  /** Disabled-style card for "Coming Soon" tiers. */
  comingSoon?: boolean;
  cta: string;
  ctaHref?: string;
  /** When set, takes precedence over ctaHref — fires on click instead
   * of navigating. Used by the pricing page to call the checkout API. */
  onCtaClick?: () => void | Promise<void>;
  /** Shown in place of the CTA label while onCtaClick is pending. */
  ctaPending?: boolean;
  features: string[];
  /** "Coming in V2" features — rendered with gold V2 pill + group header. */
  v2Features?: string[];
  v2Header?: string;
  /** Differentiator items shown line-through (e.g. "Not in Pro"). */
  missing?: string[];
}

function defaultFormat(p: number): string {
  return Number.isInteger(p) ? String(p) : p.toFixed(2);
}

function PlanCardImpl({
  name,
  nameColor,
  tag,
  price,
  formatPrice = defaultFormat,
  suffix,
  priceFootnote,
  badge,
  featured,
  comingSoon,
  cta,
  ctaHref,
  onCtaClick,
  ctaPending,
  features,
  v2Features,
  v2Header,
  missing,
}: PlanCardProps) {
  const [hover, setHover] = useState(false);

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: featured
          ? "linear-gradient(180deg, var(--color-surface-2), var(--color-surface))"
          : "var(--color-surface)",
        border: `1px solid ${featured ? "rgba(59,130,246,0.30)" : "var(--color-line)"}`,
        borderRadius: 14,
        padding: 32,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        transition: "all 0.25s",
        position: "relative",
        transform: hover && !comingSoon ? "translateY(-3px)" : "none",
        opacity: comingSoon ? 0.75 : 1,
        boxShadow: featured
          ? hover
            ? "0 24px 60px -20px rgba(59,130,246,0.30), 0 0 0 1px rgba(59,130,246,0.20)"
            : "0 10px 40px -16px rgba(0,0,0,0.4)"
          : hover
            ? "0 16px 40px -16px rgba(0,0,0,0.5)"
            : "none",
      }}
    >
      {badge ? <BadgePill>{badge}</BadgePill> : null}

      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 8,
          marginBottom: 6,
        }}
      >
        <h3
          style={{
            fontSize: 20,
            fontWeight: 600,
            color: nameColor ?? "var(--color-text)",
            margin: 0,
            letterSpacing: "-0.015em",
          }}
        >
          {name}
        </h3>
      </div>
      <div
        style={{
          fontSize: 13,
          color: "var(--color-dim)",
          marginBottom: 28,
        }}
      >
        {tag}
      </div>

      <PriceBlock
        price={price}
        formatPrice={formatPrice}
        suffix={suffix}
        footnote={priceFootnote}
        comingSoon={comingSoon}
      />

      <Btn
        variant={featured ? "blue" : "secondary"}
        size="md"
        icon={!comingSoon && !ctaPending ? <ArrowR /> : undefined}
        href={comingSoon || onCtaClick ? undefined : ctaHref}
        onClick={onCtaClick}
        className="w-full"
      >
        {ctaPending ? "Loading…" : cta}
      </Btn>

      <ul
        style={{
          listStyle: "none",
          padding: 0,
          margin: "28px 0 0",
          display: "flex",
          flexDirection: "column",
          gap: 11,
          flex: 1,
        }}
      >
        {features.map((f) => (
          <FeatureRow key={f} variant="ok">
            {f}
          </FeatureRow>
        ))}

        {v2Features && v2Features.length > 0 ? (
          <>
            <li
              style={{
                marginTop: 14,
                paddingTop: 14,
                borderTop: "1px dashed var(--color-line)",
                fontFamily: "var(--font-mono)",
                fontSize: 10,
                color: "var(--color-gold)",
                letterSpacing: "0.12em",
                fontWeight: 600,
                lineHeight: 1.5,
                textTransform: "uppercase",
              }}
            >
              {v2Header ?? "Coming in V2 (included)"}
            </li>
            {v2Features.map((f) => (
              <FeatureRow key={f} variant="v2">
                {f}
              </FeatureRow>
            ))}
          </>
        ) : null}

        {missing && missing.length > 0
          ? missing.map((f) => (
              <FeatureRow key={f} variant="missing">
                {f}
              </FeatureRow>
            ))
          : null}
      </ul>
    </div>
  );
}

export const PlanCard = memo(PlanCardImpl);

function BadgePill({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        position: "absolute",
        top: -12,
        left: "50%",
        transform: "translateX(-50%)",
        fontSize: 10,
        padding: "5px 12px",
        borderRadius: 999,
        background: "var(--color-blue)",
        color: "#fff",
        letterSpacing: "0.16em",
        fontWeight: 700,
        fontFamily: "var(--font-mono)",
      }}
    >
      {children}
    </div>
  );
}

function PriceBlock({
  price,
  formatPrice,
  suffix,
  footnote,
  comingSoon,
}: {
  price: number | null;
  formatPrice: (p: number) => string;
  suffix: string;
  footnote?: string;
  comingSoon?: boolean;
}) {
  if (comingSoon || price === null) {
    return (
      <div style={{ marginBottom: 28 }}>
        <span
          style={{
            fontSize: 22,
            fontWeight: 600,
            color: "var(--color-muted)",
            fontStyle: "italic",
          }}
        >
          Coming Soon
        </span>
        {footnote ? (
          <div
            style={{
              fontSize: 12,
              color: "var(--color-dim)",
              marginTop: 6,
            }}
          >
            {footnote}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
        <span
          style={{
            fontSize: 11,
            color: "var(--color-dim)",
            fontFamily: "var(--font-mono)",
            alignSelf: "flex-start",
            marginTop: 8,
          }}
        >
          $
        </span>
        <span
          style={{
            fontSize: 52,
            fontWeight: 600,
            color: "var(--color-text)",
            letterSpacing: "-0.03em",
            lineHeight: 1,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {formatPrice(price)}
        </span>
        <span
          style={{
            fontSize: 14,
            color: "var(--color-dim)",
            marginLeft: 4,
          }}
        >
          {suffix}
        </span>
      </div>
      {footnote ? (
        <div
          style={{
            fontSize: 12,
            color: "var(--color-dim)",
            marginTop: 6,
          }}
        >
          {footnote}
        </div>
      ) : null}
    </div>
  );
}

function FeatureRow({
  children,
  variant,
}: {
  children: ReactNode;
  variant: "ok" | "v2" | "missing";
}) {
  if (variant === "v2") {
    return (
      <li
        style={{
          fontSize: 13.5,
          color: "var(--color-muted)",
          display: "flex",
          gap: 10,
          alignItems: "flex-start",
          lineHeight: 1.5,
        }}
      >
        <span
          style={{
            width: 22,
            height: 16,
            borderRadius: 4,
            flexShrink: 0,
            marginTop: 2,
            background: "rgba(245,158,11,0.08)",
            border: "1px solid rgba(245,158,11,0.25)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "var(--font-mono)",
            fontSize: 9,
            color: "var(--color-gold)",
            letterSpacing: "0.06em",
            fontWeight: 700,
          }}
        >
          V2
        </span>
        {children}
      </li>
    );
  }

  if (variant === "missing") {
    return (
      <li
        style={{
          fontSize: 13.5,
          color: "var(--color-dim)",
          display: "flex",
          gap: 10,
          alignItems: "flex-start",
          lineHeight: 1.5,
        }}
      >
        <span
          style={{
            width: 16,
            height: 16,
            borderRadius: "50%",
            flexShrink: 0,
            marginTop: 2,
            border: "1px solid var(--color-line)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg
            width="9"
            height="9"
            viewBox="0 0 9 9"
            aria-hidden="true"
          >
            <path
              d="M2 2 L7 7 M7 2 L2 7"
              stroke="var(--color-dim)"
              strokeWidth="1.4"
              strokeLinecap="round"
            />
          </svg>
        </span>
        <s style={{ textDecorationColor: "var(--color-line)" }}>
          {children}
        </s>
      </li>
    );
  }

  return (
    <li
      style={{
        fontSize: 13.5,
        color: "var(--color-text)",
        display: "flex",
        gap: 10,
        alignItems: "flex-start",
        lineHeight: 1.5,
      }}
    >
      <span
        style={{
          width: 16,
          height: 16,
          borderRadius: "50%",
          flexShrink: 0,
          marginTop: 2,
          background: "rgba(16,185,129,0.12)",
          border: "1px solid rgba(16,185,129,0.30)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <svg
          width="9"
          height="9"
          viewBox="0 0 9 9"
          aria-hidden="true"
        >
          <path
            d="M1 4.5 L3.5 7 L8 2"
            stroke="var(--color-green)"
            strokeWidth="1.5"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      {children}
    </li>
  );
}
